import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../context/LanguageContext';
import Footer from '../Footer';
import './ServicePage.css';

const Asfaleia = () => {
  const navigate = useNavigate();
  const { t, tArray, language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  const useCasesEl = [
    {
      title: "Speech-to-Text + Intelligence",
      description: "Deployed with the Hellenic Police — 98.9% accuracy, 100+ languages, 50,000+ work hours saved annually. Real-time transcription for interviews, evidence, and investigations."
    },
    {
      title: "Cyber Anomaly Monitoring",
      description: "Core technology being developed within CyberSentinel (€5M, Digital Europe, 8 partners across 5 EU countries). AI-driven threat detection, SOC automation, and NIS2 compliance dashboards."
    },
    {
      title: "Real-Time Video Analysis",
      description: "Computer vision systems that analyze security camera feeds and automatically detect suspicious activities or incidents requiring intervention — fully EU AI Act compliant."
    },
    {
      title: "Operations Center Management",
      description: "Automated incident logging, categorization, and resource allocation with smart prioritization for emergency response coordination."
    },
    {
      title: "Railway Safety AI",
      description: "Speech transcription and analysis system deployed with the Ministry of Transport for safety-critical communications and incident investigation."
    }
  ];

  const faqsEl = [
    {
      question: "What is AI for Security & Civil Protection?",
      answer: "AI for Security includes advanced systems for video analysis, cybersecurity threat detection, operations center automation, and speech-to-text intelligence. We build systems compliant with EU AI Act, NIS2, and GDPR."
    },
    {
      question: "Do you build Predictive Policing systems?",
      answer: "We build risk analysis tools for resource planning — not predictive policing. All systems comply with AI Act prohibitions on real-time biometric identification and social scoring."
    },
    {
      question: "Are PROTOS AI systems secure and compliant?",
      answer: "Yes, all systems are built for full GDPR compliance, NIS2 alignment, and EU AI Act conformity. We are EU AI Pact Signatories and Registered EU Experts (EX2025D1322797)."
    },
    {
      question: "What AI services do you offer for Security Authorities?",
      answer: "We offer real-time video analysis, operations center automation, speech-to-text intelligence (Whisper Police — 98.9% accuracy, 100+ languages), and cyber threat detection (CyberSentinel consortium)."
    },
    {
      question: "How does AI help Civil Protection?",
      answer: "AI improves crisis response speed, analyzes threats more effectively, and optimizes resource allocation in operations centers — especially critical for emergency coordination."
    }
  ];

  const serviceKey = 'servicePage.asfaleia';
  const hasTranslation = language !== 'el';

  const title = hasTranslation ? t(`${serviceKey}.title`) : 'Τεχνητή Νοημοσύνη για την Ασφάλεια και την Πολιτική Προστασία';
  const subtitle = hasTranslation ? t(`${serviceKey}.subtitle`) : 'Ενισχύστε τις δυνατότητες ασφάλειας με προηγμένα συστήματα AI';
  const description = hasTranslation ? t(`${serviceKey}.description`) : '';

  const translatedUseCases = hasTranslation ? tArray(`${serviceKey}.useCases`) : [];
  const translatedFaqs = hasTranslation ? tArray(`${serviceKey}.faqs`) : [];

  const displayUseCases = translatedUseCases.length > 0 ? translatedUseCases : useCasesEl;
  const displayFaqs = translatedFaqs.length > 0 ? translatedFaqs : faqsEl;

  const ctaTitle = hasTranslation ? t(`${serviceKey}.ctaTitle`) : 'Ενισχύστε την Ασφάλεια με AI';
  const ctaText = hasTranslation ? t(`${serviceKey}.ctaText`) : 'Επικοινωνήστε μαζί μας για να μάθετε πώς οι λύσεις AI μπορούν να ενισχύσουν τις δυνατότητες ασφάλειας του οργανισμού σας.';

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={hasTranslation ? description : "Συστήματα AI για Αστυνομία, Πολιτική Προστασία και κρίσιμες υποδομές. Ακρίβεια, ασφάλεια δεδομένων και συμμόρφωση με GDPR."} />
        <link rel="canonical" href="https://ai-agency.gr/services/asfaleia" />
        
        {/* Schema.org Service Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "serviceType": "AI για Ασφάλεια & Επιχειρησιακά Κέντρα",
            "provider": {
              "@type": "Organization",
              "name": "PROTOS AI AGENCY",
              "url": "https://ai-agency.gr/"
            },
            "areaServed": {
              "@type": "Country",
              "name": "Greece"
            },
            "description": hasTranslation ? description : "Συστήματα AI για Αστυνομία, Πολιτική Προστασία και κρίσιμες υποδομές. Ακρίβεια, ασφάλεια δεδομένων και συμμόρφωση με GDPR."
          })}
        </script>

        {/* Schema.org FAQPage Markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": displayFaqs.map(faq => ({
              "@type": "Question",
              "name": faq.question,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.answer
              }
            }))
          })}
        </script>

        {/* Schema.org BreadcrumbList */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": hasTranslation ? t('servicePage.breadcrumb.home') : "Αρχική",
                "item": "https://ai-agency.gr/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": hasTranslation ? t('servicePage.breadcrumb.services') : "Υπηρεσίες",
                "item": "https://ai-agency.gr/#services"
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": "AI για Ασφάλεια",
                "item": "https://ai-agency.gr/services/asfaleia"
              }
            ]
          })}
        </script>
      </Helmet>

      <div className="service-page">
        <header className="service-header">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
            <button 
              onClick={() => navigate('/')}
              className="text-blue-400 hover:text-blue-300 transition-colors flex items-center space-x-2 mb-4"
            >
              <span>←</span>
              <span>{t('nav.backToHome')}</span>
            </button>
            
            {/* Breadcrumb Navigation */}
            <nav aria-label="breadcrumb" className="text-sm text-gray-400 mb-2">
              <ol className="flex items-center space-x-2">
                <li>
                  <a href="/" className="hover:text-blue-400 transition-colors">{t('servicePage.breadcrumb.home')}</a>
                </li>
                <li><span>/</span></li>
                <li>
                  <a href="/#services" className="hover:text-blue-400 transition-colors">{t('servicePage.breadcrumb.services')}</a>
                </li>
                <li><span>/</span></li>
                <li className="text-gray-300" aria-current="page">{hasTranslation ? title : t('services.asfaleia')}</li>
              </ol>
            </nav>
          </div>
        </header>

        <section className="service-hero">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
              {title}
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              {subtitle}
            </p>
          </div>
        </section>

        <section className="service-content">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <div className="prose prose-lg prose-invert max-w-none">
              {hasTranslation ? (
                <p className="text-lg text-gray-300 leading-relaxed">
                  {description}
                </p>
              ) : (
                <>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    Η ασφάλεια των πολιτών και η προστασία κρίσιμων υποδομών αποτελούν θεμελιώδεις προτεραιότητες κάθε σύγχρονου κράτους. Η Τεχνητή Νοημοσύνη προσφέρει εργαλεία που ενισχύουν τις δυνατότητες των αρχών ασφαλείας, επιτρέποντας ταχύτερη ανταπόκριση σε κρίσεις, καλύτερη ανάλυση απειλών και πιο αποτελεσματική κατανομή πόρων.
                  </p>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    Οι λύσεις μας για τον τομέα της ασφάλειας είναι σχεδιασμένες με γνώμονα τις αυστηρές απαιτήσεις των αστυνομικών αρχών, των υπηρεσιών πολιτικής προστασίας και των επιχειρησιακών κέντρων. Κατανοούμε ότι σε αυτόν τον τομέα, η αξιοπιστία, η ακρίβεια και η ασφάλεια των δεδομένων δεν είναι απλώς επιθυμητά χαρακτηριστικά—είναι απαραίτητες προϋποθέσεις.
                  </p>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    Με πιστοποιήσεις ISO 27001 για την ασφάλεια πληροφοριών, ISO 27701 για την προστασία προσωπικών δεδομένων και ISO 23894 για τη διαχείριση κινδύνων AI, διασφαλίζουμε ότι τα συστήματά μας πληρούν τα υψηλότερα διεθνή πρότυπα. Κάθε λύση σχεδιάζεται με πλήρη συμμόρφωση με το GDPR και τις ελληνικές νομοθετικές απαιτήσεις.
                  </p>
                  <p className="text-lg text-gray-300 leading-relaxed mb-6">
                    Τα συστήματά μας υποστηρίζουν ένα ευρύ φάσμα εφαρμογών: από την ανάλυση βίντεο σε πραγματικό χρόνο για την ανίχνευση ύποπτων δραστηριοτήτων, μέχρι την πρόβλεψη εγκληματικότητας με βάση ιστορικά δεδομένα, και την αυτοματοποίηση της διαχείρισης περιστατικών σε επιχειρησιακά κέντρα.
                  </p>
                  <p className="text-lg text-gray-300 leading-relaxed">
                    Ειδική έμφαση δίνουμε στην ηθική χρήση της AI στον τομέα της ασφάλειας. Τα συστήματά μας σχεδιάζονται με ενσωματωμένους μηχανισμούς διαφάνειας και λογοδοσίας, διασφαλίζοντας ότι οι αποφάσεις που λαμβάνονται με τη βοήθεια AI είναι εξηγήσιμες και ελέγξιμες.
                  </p>
                </>
              )}
            </div>
          </div>
        </section>

        <section className="use-cases-section">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
            <h2 className="text-4xl font-bold text-center mb-12 text-white">
              {t('servicePage.useCasesTitle')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayUseCases.map((useCase, index) => (
                <div key={index} className="use-case-card">
                  <h3 className="text-xl font-bold mb-3 text-green-400">{useCase.title}</h3>
                  <p className="text-gray-300">{useCase.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 bg-black/30 relative z-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold text-center mb-12 text-white">
              {t('servicePage.faqTitle')}
            </h2>
            <div className="space-y-6">
              {displayFaqs.map((faq, index) => (
                <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6">
                  <h3 className="text-xl font-bold mb-3 text-green-400">{faq.question}</h3>
                  <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Services Section */}
        <section className="py-16 relative z-10">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-8 text-white">
              {t('servicePage.relatedServicesTitle')}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <a href="/services/dimosiadioikisi" className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-blue-400/40 transition-all">
                <h3 className="text-xl font-bold mb-2 text-blue-400">{t('services.dimosiaDioikisi')}</h3>
                <p className="text-gray-400">{t('servicePage.relatedDescriptions.dimosiaDioikisi')}</p>
              </a>
              <a href="/services/erevna-anaptyxi" className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:border-cyan-400/40 transition-all">
                <h3 className="text-xl font-bold mb-2 text-cyan-400">{t('services.erevnaAnaptyxi')}</h3>
                <p className="text-gray-400">{t('servicePage.relatedDescriptions.erevnaAnaptyxi')}</p>
              </a>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
            <h2 className="text-4xl font-bold mb-6 text-white">
              {ctaTitle}
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              {ctaText}
            </p>
            <button 
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  const contactSection = document.getElementById('contact');
                  if (contactSection) {
                    contactSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }, 100);
              }}
              className="bg-gradient-to-r from-green-500 to-blue-500 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:from-green-600 hover:to-blue-600 transition-all transform hover:scale-105"
            >
              {t('servicePage.ctaButton')}
            </button>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default Asfaleia;